import { useEffect, useRef, useState } from 'react'

import { InfoButton } from 'components/buttons'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import Chevron from 'shared/svg/chevron.svg'
import { track, useTracking } from 'lib/analytics/track'
import css from './NavigationSection.module.scss'

export interface NavigationSectionLink {
  url: string
  image: string
  title: string
}

export interface NavigationSectionProps {
  contentfulId: string
  links: NavigationSectionLink[]
  slot: number
}

const NavigationSectionDisplayCount = 6

const COMPONENT_TYPE = 'navigation'

const NavigationSection = ({
  contentfulId,
  links,
  slot,
}: NavigationSectionProps) => {
  const [showAll, setShowAll] = useState<boolean>(false)
  const firstHiddenLinkRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (showAll && firstHiddenLinkRef.current) {
      firstHiddenLinkRef.current.focus()
    }
  }, [showAll])

  const tracker = useTracking()
  const [currentUser] = useCurrentUser()
  const registryProps = {
    isRegistryOwner: !!currentUser?.registryId,
    registryOwnerId: currentUser?.id,
    registryId: currentUser?.registryId,
  }

  if (links.length < NavigationSectionDisplayCount) {
    return null
  }

  return (
    <nav className={css.NavigationSection}>
      <div className={css.LinkContainer}>
        {links.map((link, index) => {
          const isHidden = index >= NavigationSectionDisplayCount && !showAll
          const position = index + 1
          const classes = [css.LinkItem, isHidden ? css.Hidden : ''].join(' ')
          const testId = `${
            isHidden ? 'Hidden' : ''
          }NavigationSectionLink${position}`

          const handleLinkClicked = () => {
            tracker.trackEvent({
              event: track.componentClicked,
              componentType: COMPONENT_TYPE,
              contentId: contentfulId,
              contentName: link.title,
              positionInList: position,
              slotPosition: slot,
              urlClicked: new URL(link.url, window.location.origin).toString(),
              ...registryProps,
            })
          }

          return (
            <a
              className={classes}
              data-testid={testId}
              href={link.url}
              key={`${link.url}-${link.title}`}
              ref={
                index === NavigationSectionDisplayCount
                  ? firstHiddenLinkRef
                  : undefined
              }
              onClick={handleLinkClicked}
            >
              <img
                alt=""
                className={css.LinkImage}
                height="150"
                loading={
                  index < NavigationSectionDisplayCount ? 'eager' : 'lazy'
                }
                src={`${link.image}?fm=jpg&fit=fill&w=300&h=300&q=50&bg=rgb:f9f6f3`}
                width="150"
              />
              <span className={css.LinkTitle}>{link.title}</span>
            </a>
          )
        })}
      </div>
      {links.length > NavigationSectionDisplayCount && (
        <div className={css.ButtonWrapper}>
          <InfoButton
            empty
            pill
            className={css.ShowAllButton}
            role="button"
            onClick={() => setShowAll(!showAll)}
          >
            Show {links.length - NavigationSectionDisplayCount}{' '}
            {showAll ? 'Less' : 'More'}
            <Chevron
              className={`${css.Chevron} ${showAll ? css.Rotated : ''}`}
            />
          </InfoButton>
        </div>
      )}
    </nav>
  )
}

export default NavigationSection
